import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import APPCONSTANTS from '../../../../constants/appConstants';
import ERRORS from '../../../../constants/error';
import { fetchHFReport, fileDownload, sendHF4ReportToKHIS } from '../../../../services/reportAPI';
import { userReviewRequest } from '../../../../store/report/actions';
import { getSiteDetailSelector } from '../../../../store/site/selectors';
import { fetchPHUCHWUserList } from '../../../../store/user/actions';
import {
  firstNameSelector,
  lastNameSelector,
  phuChwUserListCountSelector,
  phuChwUserListLoadingSelector,
  roleSelector
} from '../../../../store/user/selectors';
import { IUser } from '../../../../store/user/types';
import { getPageLocationWithParams, reportNameFormat, trackGoogleAnalyticsEvent } from '../../../../utils/commonUtils';
import toastCenter from '../../../../utils/toastCenter';
import { formatMonthName } from '../../HFReport';
import { IMatchParams, ISelectedUser } from '../types';

export const useChwList = () => {
  const dispatch = useDispatch();

  // Get params and selectors
  const { siteName, reportType, monthYear } = useParams<IMatchParams>();

  const month = monthYear.slice(0, 3);
  const year = monthYear.slice(3, 7);
  const reportName = reportType.slice(0, 3);

  const siteDetails = useSelector(getSiteDetailSelector);
  const role: any = useSelector(roleSelector);
  const firstName = useSelector(firstNameSelector) || '';
  const lastName = useSelector(lastNameSelector) || '';
  const chwUserListCount = useSelector(phuChwUserListCountSelector);
  const chwUserListloading = useSelector(phuChwUserListLoadingSelector);

  // State
  const [userList, setUserList] = useState<IUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<ISelectedUser>({});
  const [selectedAllUser, setSelectedAllUser] = useState(false);
  const [authenticationError, setAuthenticationError] = useState(false);

  // Modal states
  const [reportViewModal, setReportViewModal] = useState({ show: false, data: {} });
  const [dhisLoginModal, setDhisLoginModal] = useState({ show: false, data: {} });
  const [reviewModal, setReviewModal] = useState({ show: false, data: {} });
  const [alertModal, setAlertModal] = useState({ show: false, status: '', message: '' });

  // Derived state
  const isHF4User = role === APPCONSTANTS.ROLES.HF4_REPORT_USER;
  const pendingSelectedUsers = useMemo(
    () =>
      Object.values(selectedUser)
        .filter((u) => u.selected)
        .map((user) => ({ id: user.id, dhisId: user.userUnitId, selected: true })),
    [selectedUser]
  );
  const pendingUsersList = useMemo(
    () =>
      (userList || []).filter((u: IUser) => {
        // Check if the user is not reviewed/dhisPushed
        const notReviewedOrPushed = !u?.[isHF4User ? 'reviewed' : 'dhisPushed'];

        // If enabled property exists, check it, otherwise just use the reviewed status
        return notReviewedOrPushed && (!Object.prototype.hasOwnProperty.call(u, 'enabled') || u.enabled);
      }),
    [userList, isHF4User]
  );

  /**
   * Reset user selection state
   */
  const resetSelectionState = useCallback(() => {
    setSelectedUser({});
    setSelectedAllUser(false);
  }, []);

  /**
   * Show alert modal with status and message
   */
  const showAlertModal = useCallback((status: string, message: string) => {
    setAlertModal({
      status,
      message,
      show: true
    });
  }, []);

  /**
   * Fetch report data for a specific user
   */
  const fetchReport = useCallback(
    async (report: { id: number }) => {
      const {
        data: { entityList }
      } = await fetchHFReport({
        reportType: reportName.toUpperCase(),
        healthFacilityId: undefined,
        userId: report.id,
        userBased: true,
        year,
        month,
        hf4User: isHF4User
      });
      return entityList;
    },
    [reportName, year, month, isHF4User]
  );

  /**
   * Update a user's status in the user list
   */
  const updateUserStatus = useCallback(
    (updatedUser: IUser, keysToUpdate: { reviewed?: boolean; enabled?: boolean }) => {
      const updatedUserList = userList.map((user: IUser) =>
        user.id === updatedUser.id ? { ...user, ...keysToUpdate } : user
      );
      setUserList(updatedUserList);
    },
    [userList]
  );

  /**
   * Fetch CHW list from API
   */
  const fetchChwList = useCallback(
    (listParams: any) => {
      dispatch(
        fetchPHUCHWUserList({
          skip: (listParams.page - APPCONSTANTS.INITIAL_PAGE) * listParams.rowsPerPage,
          limit: listParams.rowsPerPage,
          tenantId: siteDetails.tenantId,
          searchTerm: listParams.searchTerm,
          isDHISStatusNeeded: true,
          hf4User: isHF4User,
          month,
          year,
          successCb: (usersList: IUser[]) => {
            if (!usersList?.length) {
              setUserList([]);
              resetSelectionState();
              return toastCenter.error(APPCONSTANTS.OOPS, APPCONSTANTS.NO_CHW_AVAILABLE);
            }

            const formattedUserList = usersList.map((user) => ({
              name: `${user.firstName} ${user.lastName}`,
              id: user.userId,
              ...user
            }));

            setUserList(formattedUserList);
          },
          failureCb: () => {
            toastCenter.error(APPCONSTANTS.OOPS, APPCONSTANTS.SITE_USERS_FETCH_ERROR);
          }
        })
      );
    },
    [dispatch, siteDetails.tenantId, month, year, isHF4User, resetSelectionState]
  );

  /**
   * View a report
   */
  const handleReportView = useCallback(
    async (report) => {
      try {
        setLoading(true);
        const entityList = await fetchReport(report);

        trackGoogleAnalyticsEvent(
          APPCONSTANTS.USER_ACTIONS.REPORT_VIEW,
          reportNameFormat(reportType),
          getPageLocationWithParams(),
          { username: `${firstName} ${lastName}`, role: role.label }
        );

        setLoading(false);

        if (!report.reviewed) {
          updateUserStatus(report, { enabled: true });
        }

        setReportViewModal({ show: true, data: entityList });
      } catch (error: any) {
        setLoading(false);
        toastCenter.error(APPCONSTANTS.ERROR, APPCONSTANTS.REPORT_NOT_FOUND_MONTH);
      }
    },
    [fetchReport, reportType, firstName, lastName, role.label, updateUserStatus]
  );

  /**
   * Download a report
   */
  const handleReportDownload = useCallback(
    async (report) => {
      const currmonth = formatMonthName(month);

      try {
        setLoading(true);
        const entityList = await fetchReport(report);

        trackGoogleAnalyticsEvent(
          APPCONSTANTS.USER_ACTIONS.REPORT_DOWNLOAD,
          reportNameFormat(reportType),
          getPageLocationWithParams(),
          { username: `${firstName} ${lastName}`, role: role.label }
        );

        setLoading(false);
        fileDownload(siteName, `_${report.name}_${reportName.toUpperCase()}_`, year + '_' + currmonth, entityList);

        if (!report.reviewed) {
          updateUserStatus(report, { enabled: true });
        }

        toastCenter.success(APPCONSTANTS.SUCCESS, APPCONSTANTS.REPORT_DOWNLOAD_SUCCESS);
      } catch (error: any) {
        setLoading(false);
        toastCenter.error(APPCONSTANTS.ERROR, APPCONSTANTS.REPORT_NOT_FOUND_MONTH);
      }
    },
    [fetchReport, firstName, lastName, month, reportName, reportType, role.label, siteName, updateUserStatus, year]
  );

  /**
   * Construct payload for submission
   */
  const constructPayload = useCallback(
    (rawData: any) => ({
      userData: selectedAllUser ? [] : pendingSelectedUsers,
      selectedAll: selectedAllUser,
      tenantId: selectedAllUser ? siteDetails.tenantId : undefined,
      month,
      year,
      hf4User: isHF4User,
      phuName: isHF4User ? undefined : `${firstName} ${lastName}`,
      reportType: reportName.toUpperCase(),
      username: isHF4User ? undefined : rawData.username,
      password: isHF4User ? undefined : rawData.password
    }),
    [
      selectedAllUser,
      pendingSelectedUsers,
      siteDetails.tenantId,
      month,
      year,
      isHF4User,
      firstName,
      lastName,
      reportName
    ]
  );

  /**
   * Send report to KHIS
   */
  const sendToKHIS = useCallback(
    async (rawDHISData) => {
      try {
        const DHISPayload = constructPayload(rawDHISData);
        setLoading(true);

        const { data } = await sendHF4ReportToKHIS(DHISPayload);

        setDhisLoginModal({ show: false, data: {} });
        resetSelectionState();
        setLoading(false);
        setAuthenticationError(false);
        updateUserStatus(rawDHISData, { reviewed: true, enabled: false });

        showAlertModal(APPCONSTANTS.SUCCESS, data.message);
      } catch (error: any) {
        setLoading(false);

        if (error.message === ERRORS.DHIS_NOT_AUTHENTICATED.message) {
          setAuthenticationError(true);
        } else {
          setDhisLoginModal({ show: false, data: {} });
          showAlertModal(APPCONSTANTS.ERROR, error.message);
        }
      }
    },
    [constructPayload, updateUserStatus, resetSelectionState, showAlertModal]
  );

  /**
   * Handle DHIS2 login form submission
   */
  const handleDhisLoginSubmit = ({ email, password }: { email: string; password: string }) => {
    sendToKHIS({
      ...dhisLoginModal.data,
      username: email,
      password
    });
  };

  /**
   * Open modal
   */
  const handleOpenActionModal = useCallback(() => {
    if (isHF4User) {
      setReviewModal({ data: selectedUser, show: true });
    } else {
      setDhisLoginModal({ data: selectedUser, show: true });
    }
  }, [isHF4User, selectedUser]);

  /**
   * Handle review submission
   */
  const handleReviewSubmit = useCallback(() => {
    // Using constructPayload for consistency
    const payload = {
      ...constructPayload({}),
      successCb: () => {
        fetchChwList({ page: 1, rowsPerPage: 10, searchTerm: '' });
        setReviewModal({ data: {}, show: false });
        resetSelectionState();
        showAlertModal(APPCONSTANTS.SUCCESS, APPCONSTANTS.USER_REVIEW_SUCCESS);
      },
      failureCb: () => {
        showAlertModal(APPCONSTANTS.ERROR, APPCONSTANTS.USER_REVIEW_FAILURE);
      }
    };

    dispatch(userReviewRequest(payload));
  }, [constructPayload, dispatch, fetchChwList, resetSelectionState, showAlertModal]);

  return {
    // State
    userList,
    loading,
    selectedUser,
    setSelectedUser,
    selectedAllUser,
    setSelectedAllUser,
    authenticationError,
    chwUserListloading,
    chwUserListCount,

    // Modal states
    reportViewModal,
    setReportViewModal,
    dhisLoginModal,
    setDhisLoginModal,
    reviewModal,
    setReviewModal,
    alertModal,
    setAlertModal,

    // Derived state
    isHF4User,
    pendingSelectedUsers,
    pendingUsersList,

    // Params and data
    month,
    year,
    reportName,
    reportType,
    firstName,
    lastName,
    role,

    // Methods
    fetchChwList,
    handleReportView,
    handleReportDownload,
    handleDhisLoginSubmit,
    handleOpenActionModal,
    handleReviewSubmit,
    updateUserStatus
  };
};
